/**
 * Generates deterministic pseudo-random floats using LCG algorithm.
 *
 * Linear Congruential Generator (Numerical Recipes parameters).
 * Same seed produces same sequence.
 *
 * @param n - Number of values to generate
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (exclusive)
 * @param seed - Random seed for reproducibility
 * @returns Iterator of random floats in [min, max)
 *
 * @example
 * ```ts
 * import { RandomFloat } from "@pdfking/tfunc"
 *
 * const randoms = [...RandomFloat(5, 0.0, 1.0, 42)]
 * // [0.123, 0.456, 0.789, ...]
 * ```
 */
export const RandomFloat = function* (
  n: number,
  min: number,
  max: number,
  seed: number
): IterableIterator<number> {
  let state = seed >>> 0 // force unsigned 32-bit

  for (let i = 0; i < n; i++) {
    // LCG parameters (Numerical Recipes)
    state = (1664525 * state + 1013904223) >>> 0
    yield (state / 0xffffffff) * (max - min) + min // scale to [min, max)
  }
}
